import Notification from '@rio-cloud/rio-uikit/Notification';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { useCreateCustomer } from '@/api/account';
import type { ChargingCardsSchemaType } from '@/components/steps/ChargingCardsStep';
import type { InvoiceDataSchemaType } from '@/components/steps/InvoicingStep/invoiceDataSchema';
import type { ShippingSchemaType } from '@/components/steps/ShippingAddressStep/ShippingAddressStep';
import { FeatureToggles } from '@/configuration/featureToggle/featureToggles';
import { OrderComplete } from '@/features/onboarding/components/OrderComplete';
import { StepContent } from '@/features/onboarding/components/StepContent';
import { StepHeader } from '@/features/onboarding/components/StepHeader';
import { useAnalytics } from '@/features/onboarding/hooks/useAnalytics';
import { useInitialState } from '@/features/onboarding/hooks/useInitialState';
import { useFeatureToggle } from '@/hooks/useFeatureToggle';
import { useStepperNavigation } from '@/hooks/useStepperNavigation';

const emptyInvoiceDataState: InvoiceDataSchemaType = {
    company: {
        customerName: '',
        companyRegistrationNumber: '',
        address: '',
        address2: '',
        postCode: '',
        city: '',
        country: '',
    },
    contact: {
        personalEmailAddress: '',
        languageOfCommunication: '',
        countryCode: '',
        phoneNumber: '',
    },
    invoicing: {
        invoiceEmailAddress: '',
        country: '',
        vat: '',
        schemeId: undefined,
        participantIdentifier: '',
    },
};

const emptyShippingAddressState: ShippingSchemaType = {
    address: '',
    address2: '',
    postCode: '',
    city: '',
    country: '',
    addressee: '',
    addressType: 'company',
};

const emptyChargingCardsState: ChargingCardsSchemaType = {
    cardsNumber: 1,
    cardsListCustomization: [{ line1: '', line2: '' }],
};

export const Onboarding = () => {
    const { account, userProfile, initialStates, countryIntl } = useInitialState();

    const createCustomerMutation = useCreateCustomer();
    const { value: isShippingAddressRedesignEnabled } = useFeatureToggle(FeatureToggles.SHIPPING_ADDRESS_REDESIGN);

    const [selectedStep, setSelectedStep] = useState(0);
    const [completedProcess, setCompletedProcess] = useState(false);

    const [disableFollowingPages, setDisableFollowingPages] = useState<number>();

    const [chargingCardsState, setChargingCardsState] = useState<ChargingCardsSchemaType>(
        initialStates?.chargingCards ?? emptyChargingCardsState
    );
    const [invoicingDataState, setInvoicingDataState] = useState<InvoiceDataSchemaType>(
        initialStates?.invoiceData ?? emptyInvoiceDataState
    );
    const [shippingAddressState, setShippingAddressState] = useState<ShippingSchemaType>(
        initialStates?.shippingAddress ?? emptyShippingAddressState
    );

    const { handleStepView, handleFlowComplete } = useAnalytics(selectedStep, completedProcess);

    const { handleNext, handlePrevious, handleStepSelect } = useStepperNavigation(
        setSelectedStep,
        setDisableFollowingPages
    );

    useEffect(() => {
        handleStepView();
    }, [selectedStep]);

    if (!account || !userProfile || !initialStates || !countryIntl) {
        return null;
    }

    const handleChargingCardState = (state: ChargingCardsSchemaType) => {
        setChargingCardsState(state);
        setDisableFollowingPages(selectedStep + 1);
    };

    const handleInvoicingDataState = (state: InvoiceDataSchemaType) => {
        setInvoicingDataState(state);
        setDisableFollowingPages(selectedStep + 1);
    };

    const handleShippingAddressState = (state: ShippingSchemaType) => {
        setShippingAddressState(state);
        setDisableFollowingPages(selectedStep + 1);
    };

    const handleReview = async () => {
        try {
            await createCustomerMutation.mutateAsync({
                chargingCardsState,
                shippingAddressState,
                supportLabel: initialStates.support.label,
                supportPhone: initialStates.support.phone,
                invoicingDataState,
                termsVersion: account?.termsAndConditions?.version ?? '',
                isShippingAddressRedesignEnabled: !!isShippingAddressRedesignEnabled,
            });
        } catch {
            return Notification.error(<FormattedMessage id='onboarding.errorMessage.onboardingFailed' />);
        }

        setCompletedProcess(true);
        handleFlowComplete({ chargingCardsState });
    };

    if (completedProcess) {
        return <OrderComplete />;
    }

    return (
        <div className='onboarding-stepper margin-y-25' data-testid='onboarding-stepper'>
            <StepHeader />
            <StepContent
                selectedStep={selectedStep}
                states={{
                    chargingCardsState,
                    invoicingDataState,
                    shippingAddressState,
                }}
                handlers={{
                    handleChargingCardState,
                    handleInvoicingDataState,
                    handleShippingAddressState,
                    handleReview,
                    handleNext,
                    handlePrevious,
                    handleStepSelect,
                }}
                isLoading={createCustomerMutation.isLoading}
                disableFollowingPages={disableFollowingPages}
                termsAndConditions={account?.termsAndConditions}
            />
        </div>
    );
};

export default Onboarding;
